import { ReactNode, useRef, useEffect, useState } from 'react';

/**
 * Viewport-aware lazy section wrapper.
 * Only renders children (triggering React.lazy chunk downloads)
 * when the placeholder enters the viewport vicinity.
 * 
 * IMPROVED: Accepts an 'id' prop to ensure that anchor links from the Navbar
 * work correctly even before the section is actually rendered.
 */
interface LazySectionProps {
  children: ReactNode;
  /** Anchor ID for navigation */
  id?: string;
  /** How far before the viewport to start loading (default: 400px for stability) */
  rootMargin?: string;
  /** Minimum height for the placeholder to avoid layout shift */
  minHeight?: string;
  /** Additional classes */
  className?: string;
}

export const LazySection = ({
  children,
  id,
  rootMargin = '400px',
  minHeight = '200px',
  className = '',
}: LazySectionProps) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, visible]);

  return (
    <div 
      ref={ref} 
      id={id} 
      className={className}
      style={{ minHeight: visible ? undefined : minHeight }}
    >
      {visible ? children : null}
    </div>
  );
};
